import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { menuPairingsFor, parsePairingPrice } from '@features/booking/utils/pairings';
import type { SelectedPairing } from '@features/booking/types/pairings';
import { useInactiveStepRecovery } from '@features/booking/hooks/useInactiveStepRecovery';
import { useForeignCardUsd } from '@features/booking/hooks/useForeignCardUsd';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';

const MaridajePage = () => {
  const { t } = useTranslation('booking');
  const { goToNext, context } = useBookingFlow();
  // Stranded-guest guard: this page can be mounted on a step that is not in
  // activeSteps (stale bookmark, shared link, back/forward after the flow
  // moved on). See useInactiveStepRecovery.
  useInactiveStepRecovery('MARIDAJE');
  const booking = useBooking();
  const restaurant = useRestaurant();

  // Same menu the flow engine gates MARIDAJE on, so the page never prices a
  // different menu than the one that made the step active.
  const selectedMenu = context.pairingMenu;

  const isPairingMandatory = !!selectedMenu?.isPairingMandatory;

  // The list arrives filtered to what this menu offers, already localized,
  // and with `price` carrying any per-menu override — no getActivePairings/
  // resolvePairing needed here (unlike restaurant.beveragePairings, which
  // other pages still resolve against).
  const pairings = useMemo(() => menuPairingsFor(selectedMenu), [selectedMenu]);

  // Initialize quantities from booking state or 0
  const [quantities, setQuantities] = useState<Record<number, number>>(() => {
    const init: Record<number, number> = {};
    for (const sp of booking.selectedPairings) {
      init[sp.id] = sp.quantity;
    }
    return init;
  });

  // Total quantity across all pairings (capped at partySize per legacy directive)
  const totalQuantity = useMemo(() => {
    return Object.values(quantities).reduce((sum, q) => sum + q, 0);
  }, [quantities]);

  const setQuantity = (id: number, qty: number) => {
    const currentQty = quantities[id] ?? 0;
    const delta = Math.max(0, qty) - currentQty;
    // Cap total at partySize (legacy: maridaje-select.js maximize check)
    if (delta > 0 && totalQuantity >= booking.diners) return;
    setQuantities((prev) => ({ ...prev, [id]: Math.max(0, qty) }));
  };

  // Mandatory validation per legacy: total must be >= partySize
  const canContinue = useMemo(() => {
    if (!isPairingMandatory) return true;
    return totalQuantity >= booking.diners;
  }, [isPairingMandatory, totalQuantity, booking.diners]);

  // Total cost
  const totalCost = useMemo(() => {
    let total = 0;
    for (const p of pairings) {
      const qty = quantities[p.id] ?? 0;
      if (qty > 0) total += parsePairingPrice(p.price) * qty;
    }
    return total;
  }, [pairings, quantities]);

  const currency = restaurant.currency || 'USD';
  // Same rule as checkout: USD when a foreign card would be charged in USD here, and every pairing has a USD price.
  const { feeBreakdown } = usePaymentFee();
  const { usdTotal, foreignViewer } = useForeignCardUsd(feeBreakdown);
  const showUsd = foreignViewer
    && usdTotal != null
    && pairings.length > 0
    && pairings.every((p) => p.altPrice != null && parsePairingPrice(p.altPrice) > 0);
  const totalCostUsd = showUsd
    ? pairings.reduce((sum, p) => sum + parsePairingPrice(p.altPrice) * (quantities[p.id] ?? 0), 0)
    : 0;

  const handleContinue = () => {
    const selected: SelectedPairing[] = [];
    for (const p of pairings) {
      const qty = quantities[p.id] ?? 0;
      if (qty > 0) {
        selected.push({
          id: p.id,
          name: p.name,
          price: parsePairingPrice(p.price),
          priceUSD: p.altPrice != null ? parsePairingPrice(p.altPrice) : undefined,
          quantity: qty,
          affectsGuarantee: p.affectsGuarantee,
        });
      }
    }
    booking.setSelectedPairings(selected);
    goToNext();
  };

  const handleSkip = () => {
    booking.setSelectedPairings([]);
    goToNext();
  };

  // The old auto-advance effect here (`if (pairings.length === 0) goToNext()`)
  // was the blank-page bug: no pairings means MARIDAJE is not in activeSteps,
  // so goToNext() resolved null and silently returned, leaving the guest on a
  // page that renders nothing. useInactiveStepRecovery above covers it — and
  // the wider condition too, since MARIDAJE.isRequired also depends on
  // selectedMenuAllowsPairings, which this effect never checked.

  if (pairings.length === 0) return null;

  return (
    <BookingPageLayout
      title={t('MARIDAJE_TITLE')}
      subtitle={t('MARIDAJE_SUBTITLE')}
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div className="w-full px-6 space-y-4">
        {pairings.map((pairing) => {
          const qty = quantities[pairing.id] ?? 0;

          return (
            <div
              key={pairing.id}
              className={`rounded-xl border p-4 transition-colors ${
                qty > 0 ? 'border-brand bg-brand/5' : 'border-border bg-card'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{pairing.name}</p>
                  {pairing.description && (
                    <p className="text-sm text-foreground/50 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pairing.description) }} />
                  )}
                  <p className="text-sm font-medium text-brand mt-1">
                    {showUsd ? 'USD' : currency} {parsePairingPrice(showUsd ? pairing.altPrice : pairing.price).toLocaleString()} {t('MARIDAJE_PER_PERSON')}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light active:scale-[0.96] transition disabled:opacity-50"
                    onClick={() => setQuantity(pairing.id, qty - 1)}
                    disabled={qty <= 0}
                  >
                    <Minus size={16} />
                  </button>
                  <div className="w-10 h-10 rounded-lg bg-foreground/5 border border-border flex items-center justify-center font-medium text-foreground text-sm tabular-nums">
                    {qty}
                  </div>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light active:scale-[0.96] transition disabled:opacity-50"
                    onClick={() => setQuantity(pairing.id, qty + 1)}
                    disabled={totalQuantity >= booking.diners}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {totalCost > 0 && (
          <div className="flex justify-between items-center pt-2 border-t border-border tabular-nums">
            <p className="text-sm text-foreground/70">{t('MARIDAJE_TOTAL')}</p>
            <p className="text-lg font-medium text-foreground">
              {showUsd ? 'USD' : currency} {(showUsd ? totalCostUsd : totalCost).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {isPairingMandatory && !canContinue && (
        <p className="text-xs text-red-500 text-center px-6 mt-2">
          {totalQuantity === 0
            ? t('MARIDAJE_MANDATORY')
            : t('MARIDAJE_NEEDS_MORE', { pairingsSelected: totalQuantity })}
        </p>
      )}

      <div className="w-full px-6 mt-12 space-y-3">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {t('MARIDAJE_CONTINUE')}
        </Button>

        {!isPairingMandatory && (
          <Button
            variant="ghost"
            className="w-full text-foreground/50 hover:text-foreground/70"
            onClick={handleSkip}
          >
            {t('MARIDAJE_SKIP')}
          </Button>
        )}
      </div>
    </BookingPageLayout>
  );
};

export default MaridajePage;
